import apiV2 from '../../api/v2';

export type InternalImageContext =
    'dx-report' | 'note' | 'vca' | 'instructions' | 'work-queue-hold';

export interface InternalImageUploadResponse {
    url: string;
    fileId: string;
    objectPath: string;
    width?: number;
    height?: number;
}

const ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

/**
 * Reads an image file's natural width/height in the browser. Used to size the
 * Tiptap image node so the editor can lay out around it without re-fetching
 * the image.
 */
function measureImage(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const dims = { width: img.naturalWidth, height: img.naturalHeight };
            URL.revokeObjectURL(url);
            resolve(dims);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to read image dimensions'));
        };
        img.src = url;
    });
}

/**
 * Uploads an inline internal image (e.g. embedded in a patient note) and
 * returns its public GCS URL plus the locally-measured dimensions.
 *
 * Throws on validation failure or network error so the caller can show a
 * toast/inline error.
 */
export async function uploadInternalImage(
    patientId: string,
    file: File,
    context: InternalImageContext
): Promise<InternalImageUploadResponse> {
    if (!ALLOWED_MIME.has(file.type)) {
        throw new Error('Image must be JPEG, PNG, or WebP.');
    }
    if (file.size > MAX_UPLOAD_BYTES) {
        throw new Error('Image exceeds the 8MB size limit.');
    }

    // Measure first — if the browser can't decode it we want to fail before
    // we burn a network round-trip on a corrupt file.
    const dims = await measureImage(file);

    const form = new FormData();
    form.append('image', file, file.name);
    form.append('context', context);

    const response = await apiV2.post<InternalImageUploadResponse>(
        `/patients/${encodeURIComponent(patientId)}/internal/images`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    return { ...response.data, width: dims.width, height: dims.height };
}

/**
 * Resolves internal image file ids to fresh signed URLs. Returns an empty
 * map on error so callers can degrade gracefully (text stays intact, images
 * may render broken).
 */
export async function resolveInternalImageUrls(
    patientId: string,
    fileIds: string[]
): Promise<Record<string, string>> {
    if (fileIds.length === 0) return {};
    const response = await apiV2.post<{ urls: Record<string, string> }>(
        `/patients/${encodeURIComponent(patientId)}/internal/images/resolve`,
        { fileIds }
    );
    return response.data?.urls ?? {};
}
